import React, { useState, useEffect } from 'react'
import dayjs from 'dayjs'
import DateSelection from './DatePicker/DateSelection'
import { ReactComponent as SwapArrows } from '../../assets/icons/swap-arrows.svg'
import './Ferry.scss'

const FerryForm = ({
  ferryRoutes,
  ferryOptions,
  promoCode,
  onChangePromoCode,
  widgetType,
  onCalendarOpen,
  journeyTypeOptions, // one way / return
  hidePromoField,
  values,
  setValues,
  passengerModalIsOpen,
  setPassengerModalIsOpen,
  passengerCount,
  submit,
  hasValidCart,
  initialEndDate,
}) => {
  const [promoCodeOpen, setPromoCodeOpen] = useState(!!promoCode)
  const availableRoutes = ferryRoutes[values.ferryId]
  const [isDateAdjusted, setIsDateAdjusted] = useState(false)

  useEffect(() => {
    if (
      !isDateAdjusted &&
      (dayjs(initialEndDate).isAfter(dayjs(values.startDate)) ||
        dayjs(initialEndDate).isSame(values.startDate))
    ) {
      setIsDateAdjusted(true)
      setValues({
        ...values,
        endDate: dayjs(initialEndDate).format('YYYY-MM-DD'),
      })
    }
  }, [isDateAdjusted, initialEndDate, values, setValues])

  const hasEndDateError = values.journeyType === 'return' && !values.endDate

  return (
    <form
      onSubmit={event => {
        event.preventDefault()
        submit()
      }}
    >
      <div className="row-group">
        {journeyTypeOptions?.length ? (
          <div className="mobile-trip-type-radios">
            {journeyTypeOptions.map(({ value, label }) => (
              <label key={value}>
                <input
                  type="radio"
                  value={value}
                  onChange={() =>
                    setValues({
                      ...values,
                      journeyType: value,
                    })
                  }
                  checked={values.journeyType === value}
                />
                <p>{label}</p>
              </label>
            ))}
          </div>
        ) : null}
        <div className="input-label">
          <label>
            <p>Booking Type</p>
            <select
              name="ferryId"
              className={`drop-down-arrow ${widgetType}-ferryIds`}
              value={values.ferryId}
              onChange={e => {
                let departureRoute = values.departureRoute
                let returnRoute = values.returnRoute
                // check we haven't selected routes not available on the newly selected ferry type
                const remainingRoutes = ferryRoutes?.[e.target.value] || []
                if (
                  !remainingRoutes?.availableOutward?.some(
                    route => route.value === departureRoute,
                  )
                ) {
                  departureRoute =
                    remainingRoutes?.availableOutward?.[0]?.value || ''
                }

                if (
                  !remainingRoutes?.availableReturn?.some(
                    route => route.value === returnRoute,
                  )
                ) {
                  returnRoute =
                    remainingRoutes?.availableOutward?.[1]?.value || ''
                }

                setValues({
                  ...values,
                  ferryId: e.target.value,
                  departureRoute,
                  returnRoute,
                })
              }}
            >
              {ferryOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
        </div>
        <div className="input-label route-selects">
          <div className="route-select">
            <label>
              <p>Outward Route</p>

              <select
                name="departureRoute"
                className="route-select-input"
                value={values.departureRoute}
                onChange={e => {
                  setValues({
                    ...values,
                    departureRoute: e.target.value,
                    returnRoute:
                      e.target.value === values.returnRoute
                        ? values.departureRoute
                        : values.returnRoute,
                  })
                }}
              >
                <option value="">Outward Route</option>
                {availableRoutes?.availableOutward?.map(route => (
                  <option key={route.value} value={route.value}>
                    {route.label}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <button
            title="Swap Outward and Return"
            type="button"
            className={`diagonal-line ${
              values.journeyType !== 'return' ? 'oneway' : ''
            }`}
            onClick={e => {
              e.preventDefault()
              setValues({
                ...values,
                departureRoute: values.returnRoute,
                returnRoute: values.departureRoute,
              })
            }}
          >
            <SwapArrows />
          </button>
          <div
            className={`route-select ${
              values.journeyType !== 'return' ? 'oneway' : ''
            }`}
          >
            <label>
              <p>Return Route</p>
              <select
                className="route-select-input"
                name="returnRoute"
                value={values.returnRoute}
                onChange={e => {
                  setValues({
                    ...values,
                    returnRoute: e.target.value,
                    departureRoute:
                      e.target.value === values.departureRoute
                        ? values.returnRoute
                        : values.departureRoute,
                  })
                }}
              >
                <option value="">Return Route</option>
                {availableRoutes?.availableReturn?.map(route => (
                  <option key={route.value} value={route.value}>
                    {route.label}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </div>
      </div>

      <div className="row-group">
        <div
          className={`input-label date-inputs ${widgetType} ${
            hasEndDateError ? 'with-error' : ''
          }`}
        >
          <DateSelection
            startDate={values.startDate}
            endDate={values.endDate}
            handleSelectDate={(key, date) => {
              const currentValues = { ...values }
              if (key === 'startDate') {
                currentValues.startDate = date
                currentValues.endDate = ''
              } else if (key === 'endDate') {
                currentValues.endDate = date
              }
              setValues({
                ...currentValues,
              })
            }}
            isOneWay={values.journeyType !== 'return'}
            widgetType={widgetType}
            onCalendarOpen={onCalendarOpen}
            hasEndDateError={hasEndDateError}
          />
        </div>
        <div className="input-label passengers">
          <label>
            <p>Passengers</p>
          </label>
          <button
            type="button"
            onClick={() => setPassengerModalIsOpen(prev => !prev)}
            className={`drop-down-arrow ${passengerModalIsOpen ? 'open' : ''}`}
          >
            <>
              {passengerCount} Passenger
              {passengerCount > 1 ? 's' : ''}
            </>
          </button>
        </div>
        {hidePromoField ? null : (
          <div className="promo-code">
            {promoCodeOpen || !!promoCode ? (
              <div className="input-label">
                <label>
                  <p>Promo Code</p>
                  <input
                    value={promoCode || ''}
                    onChange={e => onChangePromoCode(e.target.value)}
                    placeholder="Enter a Promo Code"
                    onBlur={e => setPromoCodeOpen(!!e.target.value)}
                    autoFocus
                  />
                </label>
              </div>
            ) : (
              <button type="button" onClick={() => setPromoCodeOpen(true)}>
                Add a promo code +
              </button>
            )}
          </div>
        )}

        <button
          type="submit"
          className="btn-primary"
          disabled={hasEndDateError}
        >
          {hasValidCart ? 'New Search' : 'Search'}
        </button>
      </div>
    </form>
  )
}

export default FerryForm
